import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Copy, ChatCircleText, UsersThree } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { AppHead } from "@/pages/app/Dashboard";
import { useAiCall } from "@/pages/app/Rewriter";

const fixList = (fixes) => fixes.map((f, i) => `${i + 1}. ${f.title}${f.detail ? ` — ${f.detail}` : ""}`).join("\n");

export const buildMessages = (g, name, lang, link) => {
  const fx = fixList(g.fixes || []);
  if (lang === "it") return {
    dm: `Ciao${name ? ` ${name}` : ""}, ho passato il tuo annuncio in un grader gratuito e ha preso ${g.score}/100. Ecco 3 correzioni veloci, senza impegno:\n\n${fx}\n\nVuoi la riscrittura completa? Puoi anche provare il grader da solo qui: ${link}`,
    group: `Consiglio per chi sta sistemando il proprio annuncio: ne ho passato uno in un grader gratuito (${g.score}/100). Le correzioni più veloci erano:\n\n${fx}\n\nSe volete controllare il vostro, lo strumento è gratis: ${link}`,
  };
  return {
    dm: `Hi${name ? ` ${name}` : ""}, I ran your listing through a free grader and it scored ${g.score}/100. The 3 quick fixes are below, no strings attached:\n\n${fx}\n\nWant the full rewrite? You can also run the grader yourself here: ${link}`,
    group: `Tip for anyone polishing their listing: I ran one through a free grader (${g.score}/100). The quickest wins were:\n\n${fx}\n\nFree tool if you want to check yours: ${link}`,
  };
};

const MsgCard = ({ icon: I, title, text, testid }) => {
  const { t } = useT();
  return (
    <div className="card p-6 bg-white flex flex-col" data-testid={testid}>
      <div className="flex justify-between items-center">
        <p className="font-display text-lg text-[#0F4C5C] flex items-center gap-2"><I size={20} weight="duotone" className="text-[#E36414]" /> {t(title)}</p>
        <button onClick={() => { navigator.clipboard.writeText(text); toast.success(t("Copied")); }} className="btn-ghost text-sm !py-1.5" data-testid={`${testid}-copy`}><Copy size={16} /> {t("Copy")}</button>
      </div>
      <pre className="mt-4 whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed flex-1" data-testid={`${testid}-text`}>{text}</pre>
    </div>
  );
};

export default function Outreach() {
  const { t, lang } = useT();
  const [f, setF] = useState({ listing_text: "", host_name: "", lang });
  const [res, setRes] = useState(null);
  const [hist, setHist] = useState([]);
  const [busy, call] = useAiCall();
  const link = `${window.location.origin}/grader`;
  useEffect(() => { api.get("/generations?kind=outreach").then((r) => setHist(r.data)); }, []);

  const run = async (e) => {
    e.preventDefault();
    const d = await call("/ai/outreach", f);
    if (d) { setRes(d); setHist([d, ...hist]); }
  };
  const msgs = res && buildMessages(res.output, res.input.host_name, res.input.lang, link);

  return (
    <div data-testid="outreach-page">
      <AppHead title="Outreach messages" sub="Paste a host's public listing text, get a score and 3 fixes, and copy a ready-to-send DM or group post. Personal, helpful, no spam." />
      <div className="grid xl:grid-cols-[420px_1fr] gap-6 items-start">
        <form onSubmit={run} className="card p-6 bg-white grid gap-3" data-testid="outreach-form">
          <div><span className="label">{t("Host's listing text")}</span>
            <textarea required minLength={40} rows={8} className="field resize-none" value={f.listing_text} onChange={(e) => setF({ ...f, listing_text: e.target.value })} placeholder={t("Paste the title and description the host shared in the group…")} data-testid="outreach-listing-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">{t("Host's first name")}</span><input className="field" value={f.host_name} onChange={(e) => setF({ ...f, host_name: e.target.value })} placeholder="Giulia" data-testid="outreach-name-input" /></div>
            <div><span className="label">{t("Message language")}</span>
              <select className="field" value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} data-testid="outreach-lang-select">
                <option value="it">{t("Italian")}</option><option value="en">{t("English")}</option>
              </select></div>
          </div>
          <button disabled={busy} className="btn-coral justify-center" data-testid="outreach-submit-btn"><Megaphone size={18} weight="fill" /> {busy ? t("Grading…") : t("Grade and write messages")}</button>
          <p className="text-xs text-gray-500">{t("Only use text hosts shared publicly or sent you. Up to 20 a day, not counted in your AI plan.")}</p>
        </form>
        <div className="grid gap-4">
          {busy && <div className="card p-6 bg-white space-y-2"><div className="skeleton h-8 w-24" /><div className="skeleton h-4" /><div className="skeleton h-4 w-4/5" /></div>}
          {!busy && !res && <div className="card p-8 text-gray-500 bg-white">{t("Your score and messages will appear here.")}</div>}
          {!busy && res && (
            <>
              <div className="card p-6 bg-white flex items-center gap-6 rise" data-testid="outreach-result">
                <p className="font-display text-5xl text-[#0F4C5C]" data-testid="outreach-score">{res.output.score}<span className="text-lg text-gray-500">/100</span></p>
                <p className="text-gray-700">{res.output.summary}</p>
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <MsgCard icon={ChatCircleText} title="Private message (DM)" text={msgs.dm} testid="outreach-dm" />
                <MsgCard icon={UsersThree} title="Group comment" text={msgs.group} testid="outreach-group" />
              </div>
            </>
          )}
          {hist.length > 0 && (
            <div className="card p-6 bg-white" data-testid="outreach-history">
              <p className="label">{t("Recent outreach")}</p>
              <ul className="divide-y divide-[#E2DCD0]">
                {hist.slice(0, 10).map((h) => (
                  <li key={h.gen_id}><button onClick={() => setRes(h)} className="w-full py-2.5 text-sm flex justify-between gap-4 text-left hover:text-[#E36414] transition-colors" data-testid={`outreach-history-${h.gen_id}`}>
                    <span>{h.input.host_name || t("Host")} · <span className="font-semibold">{h.output.score}/100</span></span>
                    <span className="text-gray-500">{h.created_at.slice(0, 10)}</span>
                  </button></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
